import React, { useState, useCallback, useMemo } from "react";
import { Card, Button, Input, Select, Table, Space, message, Badge, Tag, Tooltip, Divider, Typography, Spin, Progress, Popconfirm, Modal } from "antd";
import { EyeOutlined, MailOutlined, SendOutlined, ReloadOutlined, PaperClipOutlined, EditOutlined, TeamOutlined, DownOutlined, RightOutlined, InfoCircleOutlined, StopOutlined, RetweetOutlined, SyncOutlined } from "@ant-design/icons";
import { useLoadList, useQuickSearch } from "../../../hooks";
import { useTableSelection } from "../../../hooks/useTableSelection";
import { mailboxService } from "../../../api/enhancedApi";
import { formatDateTime } from "../../../utils/dateUtils";
import { useAuth } from "../../../hooks/useAuth";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import OutboxFilter from "./components/OutboxFilter";
import EmailDetailDrawer from "./components/EmailDetailDrawer";
import BulkEmailRecipientsTable from "./components/BulkEmailRecipientsTable";
import { GenericEmailSendDrawer } from "../../../components/EmailSender";
import CollapsibleFilterBar from "../../../components/common/CollapsibleFilterBar";

const { Text } = Typography;

const getUnfinishedRecipientCount = (record) => {
    if (!record?.is_bulk_email) return 0;
    const recipients = record.bulk_email_recipients || [];
    if (recipients.length > 0) {
        return recipients.filter((item) => item.status && item.status !== "SENT").length;
    }
    const summary = record.bulk_email_summary || {};
    return (summary.pending_count || 0) + (summary.failed_count || 0) + (summary.sending_count || 0) + (summary.skipped_count || 0);
};

/**
 * 发件箱列表页面
 * 展示所有用户发送的邮件
 */
const OutboxList = () => {
    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();

    // 状态定义
    const [filterParams, setFilterParams] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [selectedEmail, setSelectedEmail] = useState(null);

    // 写信功能状态
    const [composeDrawerVisible, setComposeDrawerVisible] = useState(false);

    // ====== 发件箱邮件重发：状态管理（T09 / T15B） ======
    // resendingEmailId 非 null 时打开"重发模式"的 GenericEmailSendDrawer；
    // 用 key={resendingEmailId} 强制让 drawer 在 ID 切换时重建，避免内部状态残留。
    const [resendingEmailId, setResendingEmailId] = useState(null);

    // 群发邮件展开状态
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [resumeLoadingIds, setResumeLoadingIds] = useState(() => new Set());


    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("outboxEmails", mailboxService.getOutboxList, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    // 使用表格选择Hook
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getRowProps, clearSelection, handleRowSelectionChange, getIndexColumn } = useTableSelection({
        rowKey: "id",
        dataSource: data?.items || [],
        pagination,
    });

    // 自定义行属性函数 - 只允许序号列选中，移除整行点击
    const getCustomRowProps = useCallback(
        (record, index) => {
            const originalProps = getRowProps(record, index);
            // 移除原始的 onClick 事件，只保留其他属性
            const { onClick, ...restProps } = originalProps;
            return {
                ...restProps,
                // 不添加 onClick 事件，这样点击行的其他部分不会触发选中
            };
        },
        [getRowProps],
    );

    // 全选当前页面所有邮件
    const handleSelectAllVisible = useCallback(() => {
        const visibleData = data?.items || [];
        const visibleKeys = visibleData.map((item) => item.id);
        handleRowSelectionChange(visibleKeys, visibleData);
    }, [data?.items, handleRowSelectionChange]);

    // 查看邮件详情
    const viewEmailDetail = useCallback((email) => {
        setSelectedEmail(email);
        setDetailDrawerVisible(true);
    }, []);

    // 处理表格分页变化
    const handleTableChange = useCallback((newPagination) => {
        setPagination(newPagination);
    }, []);

    // 处理搜索
    const handleSearch = useCallback(
        (values) => {
            const params = {};

            if (values.subject) {
                params.subject = values.subject;
            }

            if (values.to_address) {
                params.to_address = values.to_address;
            }

            if (values.status) {
                params.status = values.status;
            }

            // 处理日期范围
            if (values.dateRange && values.dateRange.length === 2) {
                if (values.dateRange[0]) {
                    params.start_date = values.dateRange[0].format("YYYY-MM-DD");
                }
                if (values.dateRange[1]) {
                    params.end_date = values.dateRange[1].format("YYYY-MM-DD");
                }
            }

            setFilterParams(params);
            setPagination({
                ...pagination,
                current: 1,
            });
        },
        [pagination],
    );

    // 重置搜索
    const handleReset = useCallback(() => {
        setFilterParams({});
        setPagination({
            ...pagination,
            current: 1,
        });
    }, [pagination]);

    // 筛选摘要项
    const filterSummaryItems = useMemo(() => {
        const items = [];
        if (filterParams.subject) items.push({ label: "主题", value: filterParams.subject.length > 12 ? filterParams.subject.substring(0, 12) + "..." : filterParams.subject, fullValue: filterParams.subject });
        if (filterParams.to_address) items.push({ label: "收件人", value: filterParams.to_address });
        if (filterParams.status) {
            const statusMap = { PendingSend: "待发送", Sent: "已发送", Failed: "发送失败" };
            items.push({ label: "状态", value: statusMap[filterParams.status] || filterParams.status });
        }
        if (filterParams.start_date || filterParams.end_date) {
            items.push({ label: "日期", value: `${filterParams.start_date || "..."} ~ ${filterParams.end_date || "..."}` });
        }
        return items;
    }, [filterParams]);

    // 快捷搜索（邮件主题）
    const quickSearch = useQuickSearch({ setFilterParams, setPagination, paramKey: 'subject' });

    // 快捷状态筛选
    const handleQuickStatusChange = useCallback((val) => {
        setFilterParams((prev) => {
            const next = { ...prev };
            if (val) {
                next.status = val;
            } else {
                delete next.status;
            }
            return next;
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    // 终止群发邮件发送（必须在 early return 之前声明，遵守 Hooks 规则）
    const handleCancelMailJob = useCallback(
        async (jobId) => {
            try {
                await mailboxService.cancelMailJob(jobId);
                message.success("已发送终止信号，邮件将在当前正在发送的邮件完成后停止");
                refetch();
            } catch (error) {
                const detail = error?.response?.data?.detail || "终止失败";
                message.error(detail);
            }
        },
        [refetch],
    );

    const handleResumeUnfinished = useCallback(
        (record) => {
            const unfinishedCount = getUnfinishedRecipientCount(record);
            if (!record?.id || unfinishedCount <= 0) return;

            Modal.confirm({
                title: "补发未完成收件人",
                icon: <SyncOutlined style={{ color: "#1677ff" }} />,
                content: (
                    <div>
                        <p>系统将只补发未完成的 {unfinishedCount} 个收件人，已发送成功的收件人不会重复发送。</p>
                        <p style={{ color: "#8c8c8c", fontSize: 12, margin: 0 }}>
                            如果仍有收件人处于活跃发送中，后端会拒绝本次补发，请稍后再试。
                        </p>
                    </div>
                ),
                okText: "开始补发",
                cancelText: "取消",
                onOk: async () => {
                    setResumeLoadingIds((prev) => new Set(prev).add(record.id));
                    try {
                        const response = await mailboxService.resumeUnfinishedMailJob(record.id);
                        const result = response?.data || response;
                        if (result?.started) {
                            message.success(result.message || "已启动补发未完成收件人");
                            refetch();
                            setTimeout(refetch, 3000);
                        } else {
                            message.info(result?.message || "没有需要补发的收件人");
                            refetch();
                        }
                    } catch (error) {
                        const detail = error?.response?.data?.detail || "补发未完成收件人失败";
                        message.error(detail);
                    } finally {
                        setResumeLoadingIds((prev) => {
                            const next = new Set(prev);
                            next.delete(record.id);
                            return next;
                        });
                    }
                },
            });
        },
        [refetch],
    );

    // ====== 发件箱邮件重发：D3 代发确认 + 打开重发抽屉（T09 / T15B / FR-015） ======
    //
    // 用 useCallback 包住，让 EmailDetailDrawer 和列表行重发按钮共享同一份逻辑。
    // 调用方传入完整的 email/record 对象（含 sender_user_id / sender_display_name 等列表行字段）。
    //
    // 决策路径：
    //   1. record.sender_user_id == null（系统自动转发邮件）→ 跳过弹窗，直接打开
    //   2. record.sender_user_id == current_user.id（自己发的邮件重发自己）→ 跳过弹窗，直接打开
    //   3. 其它情况（代发场景）→ Modal.confirm 让用户确认是否要重发别人的邮件
    const handleResend = useCallback(
        (record) => {
            if (!record || !record.id) return;
            // 关闭详情抽屉（若打开），避免编辑器和详情两个弹窗叠加
            setDetailDrawerVisible(false);

            const senderId = record.sender_user_id;
            const isCrossUser = senderId != null && user?.id != null && senderId !== user.id;

            const openResendDrawer = () => {
                setResendingEmailId(record.id);
            };

            if (isCrossUser) {
                const senderName = record.sender_display_name || record.sender_username || "其他用户";
                Modal.confirm({
                    title: "代发确认",
                    icon: <RetweetOutlined style={{ color: "#fa8c16" }} />,
                    content: (
                        <div>
                            <p>这封邮件由 <strong>{senderName}</strong> 发出，你确认要重发吗？</p>
                            <p style={{ color: "#8c8c8c", fontSize: 12, margin: 0 }}>
                                重发后的新邮件会归属到你的账号下；原邮件不会被修改。
                            </p>
                        </div>
                    ),
                    okText: "确认重发",
                    cancelText: "取消",
                    onOk: openResendDrawer,
                });
            } else {
                openResendDrawer();
            }
        },
        [user?.id],
    );

    // 如果认证信息还在加载中，显示加载状态
    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载用户信息...</p>
                    </div>
                </Card>
            </div>
        );
    }

    // 权限检查
    if (!hasPermission("mail:read")) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <h3>无权限访问</h3>
                        <p>您没有查看发件箱的权限，请联系管理员。</p>
                        <p style={{ color: "#666", fontSize: "12px", marginTop: 16 }}>
                            当前用户: {user?.username || "未知"} | 权限列表: {user?.permissions?.length ? user.permissions.join(", ") : "无权限"}
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    // 获取邮件状态配置
    const getStatusConfig = (status) => {
        const statusMap = {
            PendingSend: { status: "processing", text: "发送中", color: "blue" },
            Sent: { status: "success", text: "已发送", color: "green" },
            Failed: { status: "error", text: "发送失败", color: "red" },
            PartiallySent: { status: "warning", text: "已终止", color: "orange" },
            Cancelled: { status: "warning", text: "已取消", color: "orange" },
        };
        return statusMap[status] || { status: "default", text: status || "未知", color: "default" };
    };

    // 表格列定义
    const columns = [
        getIndexColumn("序号", 60),
        {
            title: "邮件主题",
            dataIndex: "subject",
            key: "subject",
            ellipsis: true,
            width: "25%",
            render: (text, record) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        viewEmailDetail(record);
                    }}
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                >
                    {record.has_attachments && (
                        <Tooltip title={`${record.attachment_count || 1} 个附件`}>
                            <PaperClipOutlined style={{ color: "#999" }} />
                        </Tooltip>
                    )}
                    <span>{text || "无主题"}</span>
                </a>
            ),
        },
        {
            title: (
                <span>
                    操作人{" "}
                    <Tooltip title="执行发送操作的系统用户，非邮件头中的发件人地址">
                        <InfoCircleOutlined style={{ color: "#999", fontSize: 12, cursor: "help" }} />
                    </Tooltip>
                </span>
            ),
            dataIndex: "sender_username",
            key: "sender_username",
            ellipsis: true,
            width: "8%",
            render: (text) => <span>{text || "-"}</span>,
        },
        {
            title: "发件邮箱",
            dataIndex: "from_address",
            key: "from_address",
            ellipsis: true,
            width: "12%",
            render: (text) => (
                <Tooltip title={text}>
                    <span>{text || "-"}</span>
                </Tooltip>
            ),
        },
        {
            title: "收件人",
            dataIndex: "to_address",
            key: "to_address",
            ellipsis: true,
            width: "20%",
            render: (text, record) => {
                // 群发邮件：从 bulk_email_recipients 获取收件人信息
                if (record.is_bulk_email) {
                    const recipients = record.bulk_email_recipients || [];
                    if (recipients.length === 0) {
                        return <Text type="secondary">-</Text>;
                    }
                    // 只取 TO 类型的收件人显示
                    const toRecipients = recipients.filter((r) => r.recipient_type === "TO");
                    const displayRecipients = toRecipients.length > 0 ? toRecipients : recipients;
                    const firstRecipient = displayRecipients[0];
                    const moreCount = displayRecipients.length - 1;
                    const allEmails = displayRecipients.map((r) => r.recipient_email).join(", ");

                    return (
                        <Tooltip title={allEmails}>
                            <span>
                                {firstRecipient.recipient_email}
                                {moreCount > 0 && <Text type="secondary"> (+{moreCount}人)</Text>}
                            </span>
                        </Tooltip>
                    );
                }

                // 普通邮件：使用 to_address 字段
                if (!text) return <Text type="secondary">-</Text>;
                // 如果有多个收件人，只显示第一个
                const emails = text.split(/[,;]/);
                const firstEmail = emails[0].trim();
                const moreCount = emails.length - 1;
                return (
                    <Tooltip title={text}>
                        <span>
                            {firstEmail}
                            {moreCount > 0 && <Text type="secondary"> (+{moreCount})</Text>}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status, record) => {
                const config = getStatusConfig(status);
                const tooltipContent = record.error_message ? `错误: ${record.error_message}` : null;
                return (
                    <Tooltip title={tooltipContent}>
                        <Badge status={config.status} text={config.text} />
                    </Tooltip>
                );
            },
        },
        {
            title: "群发",
            dataIndex: "is_bulk_email",
            key: "is_bulk_email",
            width: 150,
            render: (isBulk, record) => {
                if (!isBulk) return <Text type="secondary">-</Text>;

                const summary = record.bulk_email_summary || {};
                const total = summary.total_recipients || record.bulk_total_recipients || 0;
                const sent = summary.sent_count || 0;
                const failed = summary.failed_count || 0;
                const pending = summary.pending_count || 0;
                const sending = summary.sending_count || 0;
                const skipped = summary.skipped_count || 0;
                const progressPercent = total > 0 ? Math.round(((sent + failed + skipped) / total) * 100) : 0;
                const hasRecipients = record.bulk_email_recipients?.length > 0;
                const isExpanded = expandedRowKeys.includes(record.id);

                // 点击切换展开/收缩
                const handleToggleExpand = (e) => {
                    e.stopPropagation();
                    if (!hasRecipients) return;
                    setExpandedRowKeys((prev) =>
                        prev.includes(record.id) ? prev.filter((key) => key !== record.id) : [...prev, record.id]
                    );
                };

                return (
                    <Tooltip title={hasRecipients ? `点击${isExpanded ? "收起" : "展开"}查看收件人详情` : "群发邮件"} placement="top">
                        <div
                            onClick={handleToggleExpand}
                            style={{
                                minWidth: 110,
                                cursor: hasRecipients ? "pointer" : "default",
                                padding: "4px 8px",
                                borderRadius: 6,
                                transition: "all 0.2s ease",
                                background: isExpanded ? "rgba(114, 46, 209, 0.08)" : "transparent",
                                border: hasRecipients ? "1px dashed #d9d9d9" : "1px solid transparent",
                            }}
                            onMouseEnter={(e) => {
                                if (hasRecipients) {
                                    e.currentTarget.style.background = "rgba(114, 46, 209, 0.12)";
                                    e.currentTarget.style.borderColor = "#722ed1";
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = isExpanded ? "rgba(114, 46, 209, 0.08)" : "transparent";
                                e.currentTarget.style.borderColor = hasRecipients ? "#d9d9d9" : "transparent";
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
                                {hasRecipients && (
                                    isExpanded ? <DownOutlined style={{ fontSize: 10, color: "#722ed1" }} /> : <RightOutlined style={{ fontSize: 10, color: "#722ed1" }} />
                                )}
                                <TeamOutlined style={{ color: "#722ed1" }} />
                                <Text style={{ fontSize: 12 }}>{total} 人</Text>
                                {sent > 0 && <Text type="success" style={{ fontSize: 11 }}>({sent}成功)</Text>}
                                {failed > 0 && <Text type="danger" style={{ fontSize: 11 }}>({failed}失败)</Text>}
                                {sending > 0 && <Text type="secondary" style={{ fontSize: 11 }}>({sending}发送中)</Text>}
                                {skipped > 0 && <Text type="warning" style={{ fontSize: 11 }}>({skipped}跳过)</Text>}
                            </div>
                            {total > 0 && (
                                <Progress
                                    percent={progressPercent}
                                    size="small"
                                    showInfo={false}
                                    status={failed > 0 && pending === 0 && sending === 0 ? "exception" : pending > 0 || sending > 0 ? "active" : "success"}
                                    style={{ margin: 0, width: "100%" }}
                                />
                            )}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: "创建时间",
            dataIndex: "created_at",
            key: "created_at",
            width: 160,
            render: (text) => (text ? formatDateTime(text) : "-"),
        },
        {
            title: "发送时间",
            dataIndex: "sent_at",
            key: "sent_at",
            width: 160,
            render: (text) => (text ? formatDateTime(text) : <Text type="secondary">未发送</Text>),
        },
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 230,
            render: (_, record) => {
                // ====== 发件箱邮件重发：列表行按钮显隐 / 置灰规则（T09） ======
                // 数据源：record 来自 GET /v1/mail/outbox 列表项，T04 已补齐 template_available / template_code / sender_user_id / sender_display_name
                //
                // 规则优先级（spec T09 描述）：
                //   (a) 无 mail:send 权限 → 不渲染按钮（FR-010 前端门控）
                //   (b) status === 'PendingSend' → 不渲染按钮（FR-006）
                //   (c) status in Sent/Failed/PartiallySent/Cancelled && template_code 非空且 template_available=false
                //       → 显示但置灰、Tooltip 提示模板已下线（B2 / FR-013）
                //   (d) 其余情况 → 正常可点
                const canResend = hasPermission("mail:send");
                const showResend = canResend && record.status !== "PendingSend";
                const unfinishedCount = getUnfinishedRecipientCount(record);
                const showResumeUnfinished = canResend && record.is_bulk_email && unfinishedCount > 0;
                const isResumeLoading = resumeLoadingIds.has(record.id);

                let resendDisabled = false;
                let resendTooltip = null;
                if (showResend && record.template_code && record.template_available === false) {
                    resendDisabled = true;
                    resendTooltip = `原模板（${record.template_code}）已下线，无法重发`;
                }

                return (
                    <Space size="small" className="table-actions">
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                viewEmailDetail(record);
                            }}
                        >
                            详情
                        </Button>
                        {showResend && (
                            <Tooltip title={resendTooltip} placement="topRight">
                                <span style={{ display: "inline-block", cursor: resendDisabled ? "not-allowed" : "pointer" }}>
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<RetweetOutlined />}
                                        disabled={resendDisabled}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleResend(record);
                                        }}
                                        style={resendDisabled ? { pointerEvents: "none" } : undefined}
                                    >
                                        重发
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        {showResumeUnfinished && (
                            <Button
                                type="link"
                                size="small"
                                icon={<SyncOutlined />}
                                loading={isResumeLoading}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleResumeUnfinished(record);
                                }}
                            >
                                补发({unfinishedCount})
                            </Button>
                        )}
                        {record.is_bulk_email && record.status === "PendingSend" && (
                            <Popconfirm
                                title="确定要终止发送吗？"
                                description="已发送的邮件不会被撤回，仅停止发送剩余收件人。"
                                onConfirm={(e) => {
                                    e?.stopPropagation();
                                    handleCancelMailJob(record.id);
                                }}
                                onCancel={(e) => e?.stopPropagation()}
                                okText="确定终止"
                                cancelText="取消"
                                okButtonProps={{ danger: true }}
                            >
                                <Button
                                    type="link"
                                    size="small"
                                    danger
                                    icon={<StopOutlined />}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    终止
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div className="page-title-compact">
                    <SendOutlined style={{ marginRight: 8 }} />
                    发件箱
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={refetch} loading={isLoading}>
                        刷新
                    </Button>
                    <Button type="primary" icon={<EditOutlined />} onClick={() => setComposeDrawerVisible(true)}>
                        写信
                    </Button>
                </Space>
            </div>

            {/* 搜索过滤 */}
            <CollapsibleFilterBar
                summaryItems={filterSummaryItems}
                onReset={handleReset}
                drawerTitle="发件箱 - 筛选条件"
                quickControls={
                    <Space size={8} align="center">
                        <Input
                            placeholder="搜索邮件主题..."
                            style={{ width: 200, height: 26 }}
                            {...quickSearch.inputProps}
                        />
                        <Select
                            placeholder="状态"
                            size="small"
                            style={{ width: 100, height: 26 }}
                            allowClear
                            value={filterParams.status || undefined}
                            onChange={handleQuickStatusChange}
                            options={[
                                { value: "PendingSend", label: "待发送" },
                                { value: "Sent", label: "已发送" },
                                { value: "Failed", label: "失败" },
                            ]}
                        />
                    </Space>
                }
            >
                <OutboxFilter onSearch={handleSearch} onReset={handleReset} autoApply />
            </CollapsibleFilterBar>

            {/* 批量操作工具栏 - sticky 定位确保滚动时可见 */}
            {selectedRowKeys.length > 0 && (
                <div className="batch-operations-info">
                    <span style={{ marginRight: 8, fontSize: "12px" }}>
                        已选择 <span style={{ fontWeight: "bold" }}>{selectedRowKeys.length}</span> 封邮件
                    </span>
                    <Divider type="vertical" style={{ margin: "0 4px" }} />
                    <Button size="small" type="link" onClick={handleSelectAllVisible} style={{ padding: "0 4px" }}>
                        全选当前页
                    </Button>
                    <Divider type="vertical" style={{ margin: "0 4px" }} />
                    <Button size="small" type="link" onClick={clearSelection} style={{ padding: "0 4px" }}>
                        取消选择
                    </Button>
                </div>
            )}

            {/* 邮件列表 */}
            <Card className="data-card">


                <Table
                    size="small"
                    columns={columns}
                    dataSource={data?.items || []}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 封邮件`,
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1100 }}
                    rowSelection={{
                        ...rowSelection,
                        columnWidth: 6,
                    }}
                    ref={tableRef}
                    onRow={getCustomRowProps}
                    expandable={{
                        expandedRowKeys,
                        onExpandedRowsChange: setExpandedRowKeys,
                        expandedRowRender: (record) => (
                            <BulkEmailRecipientsTable
                                recipients={record.bulk_email_recipients || []}
                                summary={record.bulk_email_summary || {}}
                                compact={true}
                                showSummary={false}
                            />
                        ),
                        rowExpandable: (record) => record.is_bulk_email && record.bulk_email_recipients?.length > 0,
                        // 完全隐藏展开图标列，展开功能集成到"群发"列
                        showExpandColumn: false,
                    }}
                />
            </Card>

            {/* 邮件详情抽屉 */}
            <EmailDetailDrawer
                visible={detailDrawerVisible}
                onClose={() => setDetailDrawerVisible(false)}
                email={selectedEmail}
                onResend={handleResend}
                onResumeUnfinished={handleResumeUnfinished}
            />

            {/* 写信抽屉 */}
            <GenericEmailSendDrawer
                visible={composeDrawerVisible}
                onClose={() => setComposeDrawerVisible(false)}
                onSuccess={() => {
                    setComposeDrawerVisible(false);
                    refetch();
                }}
            />

            {/* ====== 发件箱邮件重发：重发抽屉（T09） ======
              * 用 key={resendingEmailId} 强制让 drawer 在 ID 变化时重建（避免组件内部 state 残留导致下一次重发用上次的种子）
              * visible 用 !!resendingEmailId 派生——关闭时把 resendingEmailId 设回 null
              */}
            {resendingEmailId != null && (
                <GenericEmailSendDrawer
                    key={`resend-${resendingEmailId}`}
                    visible={true}
                    resendSourceEmailId={resendingEmailId}
                    onClose={() => setResendingEmailId(null)}
                    onSuccess={() => {
                        setResendingEmailId(null);
                        refetch();
                    }}
                />
            )}
        </div>
    );
};

export default OutboxList;
